import axios from 'axios'
import {ChangeEvent, useCallback, useEffect, useMemo, useState} from 'react'
import {useSelector} from 'react-redux'

import {Box, Button, FormControl, InputLabel, MenuItem, Select, Typography} from '@mui/material'
import Swal from 'sweetalert2'

import {chargeBackHost} from 'GLOBAL/envVariables'

type TableRow = Record<string, unknown>

type QueryResult = {
  key: string
  label: string
  data: TableRow[]
}

type UploadResponse = {
  message: string
  fileName?: string
  uploadData?: TableRow[]
  queryResults?: QueryResult[]
}

type DataTab = {
  key: string
  label: string
  data: TableRow[]
}

type ManufacturerOption = {
  cooperIdx: number
  cooperName: string
}

type AccountOption = {
  id: string
}

const DeviceUpdate = () => {
  const officeValue = useSelector((state: any) => state?.officeInfo.value)

  const [manufacturers, setManufacturers] = useState<ManufacturerOption[]>([])
  const [selectedManufacturer, setSelectedManufacturer] = useState<number | ''>('')

  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [isUploading, setIsUploading] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')

  const [activeTabKey, setActiveTabKey] = useState('upload')

  const [tabs, setTabs] = useState<DataTab[]>([
    {
      key: 'upload',
      label: '업로드 데이터',
      data: [],
    },
  ])

  const [accounts, setAccounts] = useState<AccountOption[]>([])
  const [selectedAccount, setSelectedAccount] = useState('')

  /**
   * 제조사 조회
   *
   * 현재 선택된 사업소 기준으로 제조사 목록을 가져옵니다.
   */
  const fetchManufacturers = useCallback(async () => {
    if (!officeValue) {
      setManufacturers([])
      setSelectedManufacturer('')
      return
    }

    try {
      const res = await axios.get(`${chargeBackHost}/api/manufacturer/select`, {
        params: {
          office_id: officeValue,
        },
      })

      if (res.data.success) {
        const mapped: ManufacturerOption[] = res.data.data
          .filter((item: any) => item.used_status === true)
          .sort((a: any, b: any) => Number(a.cooper_idx) - Number(b.cooper_idx))
          .map((item: any) => ({
            cooperIdx: item.cooper_idx,
            cooperName: item.cooper_name,
          }))

        setManufacturers(mapped)

        /**
         * 제조사가 존재하면 첫 번째 제조사를 기본 선택합니다.
         */
        if (mapped.length > 0) {
          setSelectedManufacturer(mapped[0].cooperIdx)
        } else {
          setSelectedManufacturer('')
        }
      }
    } catch (err) {
      console.error(err)

      setManufacturers([])
      setSelectedManufacturer('')

      Swal.fire({
        icon: 'error',
        title: '조회 실패',
        text: '제조사 목록을 불러오지 못했습니다.',
        confirmButtonText: '확인',
        confirmButtonColor: '#d32f2f',
      })
    }
  }, [officeValue])

  const fetchAccounts = useCallback(
    async (cooperIdx: number | '') => {
      if (!officeValue || cooperIdx === '') {
        setAccounts([])
        setSelectedAccount('')
        return
      }

      try {
        const res = await axios.get(`${chargeBackHost}/api/account/select`, {
          params: {
            office_id: officeValue,
            cooper_idx: cooperIdx,
          },
        })

        if (res.data.success) {
          const mapped: AccountOption[] = res.data.data.map((item: any) => ({
            id: item.id,
          }))

          setAccounts(mapped)

          if (mapped.length > 0) {
            setSelectedAccount(mapped[0].id)
          } else {
            setSelectedAccount('')
          }
        }
      } catch (err) {
        console.error(err)

        setAccounts([])
        setSelectedAccount('')

        Swal.fire({
          icon: 'error',
          title: '조회 실패',
          text: 'Account ID 목록을 불러오지 못했습니다.',
          confirmButtonText: '확인',
          confirmButtonColor: '#d32f2f',
        })
      }
    },
    [officeValue]
  )

  /**
   * 사업소 변경 시 제조사 목록 재조회
   */
  useEffect(() => {
    fetchManufacturers()
  }, [fetchManufacturers])

  const activeTab = useMemo(() => {
    return tabs.find((tab) => tab.key === activeTabKey) ?? tabs[0]
  }, [tabs, activeTabKey])

  const columns = useMemo(() => {
    const columnSet = new Set<string>()

    activeTab?.data.forEach((row) => {
      Object.keys(row).forEach((column) => {
        columnSet.add(column)
      })
    })

    return Array.from(columnSet)
  }, [activeTab])

  const resetDataTabs = () => {
    setTabs([
      {
        key: 'upload',
        label: '업로드 데이터',
        data: [],
      },
    ])

    setActiveTabKey('upload')
    setErrorMessage('')
  }

  const handleManufacturerChange = async (event: any) => {
    const cooperIdx = Number(event.target.value)

    setSelectedManufacturer(cooperIdx)
    setSelectedAccount('')
    setAccounts([])

    resetDataTabs()

    await fetchAccounts(cooperIdx)
  }

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null

    setSelectedFile(file)
    resetDataTabs()
  }

  const handleUpload = async () => {
    if (!selectedManufacturer) {
      Swal.fire({
        icon: 'warning',
        title: '제조사 선택',
        text: '제조사를 선택해주세요.',
        confirmButtonText: '확인',
      })

      return
    }

    if (!selectedFile) {
      Swal.fire({
        icon: 'warning',
        title: '파일 선택',
        text: '업로드할 엑셀 파일을 선택해주세요.',
        confirmButtonText: '확인',
      })

      return
    }

    const formData = new FormData()

    formData.append('file', selectedFile)

    const queryParams = new URLSearchParams({
      office_id: String(officeValue),
      manufacturer: String(selectedManufacturer),
      id: String(selectedAccount),
    })

    try {
      setIsUploading(true)
      setErrorMessage('')

      const response = await fetch(
        `${chargeBackHost}/api/device/upload?${queryParams.toString()}`,
        {
          method: 'POST',
          body: formData,
        }
      )

      const result: UploadResponse = await response.json()

      if (!response.ok) {
        throw new Error(result.message || '파일 업로드에 실패했습니다.')
      }

      const uploadTab: DataTab = {
        key: 'upload',
        label: '업로드 데이터',
        data: Array.isArray(result.uploadData) ? result.uploadData : [],
      }

      const resultTabs: DataTab[] = Array.isArray(result.queryResults)
        ? result.queryResults.map((queryResult, index) => ({
            key: `${queryResult.key}-${index}`,
            label: queryResult.label,
            data: Array.isArray(queryResult.data) ? queryResult.data : [],
          }))
        : []

      setTabs([uploadTab, ...resultTabs])

      if (resultTabs.length > 0) {
        setActiveTabKey(resultTabs[0].key)
      } else {
        setActiveTabKey('upload')
      }
    } catch (error) {
      console.error('파일 업로드 오류:', error)

      const message = error instanceof Error ? error.message : '파일 업로드 중 오류가 발생했습니다.'

      setErrorMessage(message)
    } finally {
      setIsUploading(false)
    }
  }

  const renderCellValue = (value: unknown): string => {
    if (value === null || value === undefined) {
      return ''
    }

    if (typeof value === 'object') {
      return JSON.stringify(value)
    }

    return String(value)
  }

  return (
    <Box display='flex' flexDirection='column' minWidth={0} height='100%'>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
          gap: 2,
          flexWrap: 'wrap',
        }}
      >
        <Box>
          <Typography variant='h6'>장비 업데이트</Typography>

          <Typography
            variant='body2'
            sx={{
              fontSize: '14px',
              fontWeight: '500',
              color: 'text.secondary',
            }}
          >
            제조사를 선택하고 엑셀 파일을 업로드하여 장비 정보를 업데이트합니다.
          </Typography>
        </Box>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            flexWrap: 'wrap',
          }}
        >
          {/* 제조사 선택 */}
          <FormControl size='small' sx={{minWidth: 200}}>
            <InputLabel id='manufacturer-select-label'>제조사</InputLabel>

            <Select
              labelId='manufacturer-select-label'
              value={selectedManufacturer}
              label='제조사'
              onChange={handleManufacturerChange}
              disabled={isUploading || manufacturers.length === 0}
            >
              {manufacturers.map((manufacturer) => (
                <MenuItem key={manufacturer.cooperIdx} value={manufacturer.cooperIdx}>
                  {manufacturer.cooperName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size='small' sx={{minWidth: 200}}>
            <InputLabel id='account-select-label'>Account ID</InputLabel>

            <Select
              labelId='account-select-label'
              value={selectedAccount}
              label='Account ID'
              onChange={(e) => setSelectedAccount(String(e.target.value))}
              disabled={isUploading || accounts.length === 0}
            >
              {accounts.map((account) => (
                <MenuItem key={account.id} value={account.id}>
                  {account.id}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* 파일 선택 */}
          <Button
            variant='outlined'
            component='label'
            disabled={isUploading}
            sx={{
              height: 40,
              whiteSpace: 'nowrap',
            }}
          >
            파일 선택
            <input hidden type='file' accept='.xlsx,.xls' onChange={handleFileChange} />
          </Button>

          <Typography
            variant='body2'
            sx={{
              maxWidth: 250,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              color: 'text.secondary',
            }}
          >
            {selectedFile ? selectedFile.name : '선택된 파일이 없습니다.'}
          </Typography>

          <Button
            variant='contained'
            onClick={handleUpload}
            disabled={!selectedFile || !selectedManufacturer || isUploading}
            sx={{
              height: 40,
              minWidth: 100,
              whiteSpace: 'nowrap',
            }}
          >
            {isUploading ? '업로드 중...' : '업로드'}
          </Button>
        </Box>
      </Box>

      {errorMessage && (
        <Box
          sx={{
            mb: 2,
            border: '1px solid #fecaca',
            backgroundColor: '#fef2f2',
            color: '#dc2626',
            borderRadius: 1,
            px: 2,
            py: 1.5,
            fontSize: '14px',
          }}
        >
          {errorMessage}
        </Box>
      )}

      {/* 결과 탭 */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-end',
          overflowX: 'auto',
          borderBottom: '1px solid #d1d5db',
        }}
      >
        {tabs.map((tab) => {
          const isActive = activeTabKey === tab.key

          return (
            <Button
              key={tab.key}
              onClick={() => setActiveTabKey(tab.key)}
              sx={{
                minWidth: 130,
                borderRadius: '8px 8px 0 0',
                border: '1px solid',
                borderColor: isActive ? '#d1d5db' : 'transparent',
                borderBottomColor: isActive ? '#fff' : 'transparent',
                backgroundColor: isActive ? '#fff' : '#f3f4f6',
                color: isActive ? '#111827' : '#6b7280',
                fontWeight: 600,
                textTransform: 'none',
                mb: '-1px',
              }}
            >
              {tab.label} ({tab.data.length.toLocaleString()})
            </Button>
          )
        })}
      </Box>

      {/* 결과 테이블 */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: 'auto',
          border: '1px solid #d1d5db',
          borderTop: 0,
          backgroundColor: '#fff',
        }}
      >
        {!activeTab || activeTab.data.length === 0 ? (
          <Box
            sx={{
              minHeight: 160,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'text.secondary',
              fontSize: '14px',
            }}
          >
            표시할 데이터가 없습니다.
          </Box>
        ) : (
          <table
            style={{
              minWidth: 'max-content',
              borderCollapse: 'collapse',
              fontSize: '14px',
            }}
          >
            <thead>
              <tr>
                <th
                  style={{
                    padding: '12px 16px',
                    borderRight: '1px solid #d1d5db',
                    borderBottom: '1px solid #d1d5db',
                    backgroundColor: '#f3f4f6',
                    whiteSpace: 'nowrap',
                  }}
                >
                  No.
                </th>

                {columns.map((column) => (
                  <th
                    key={column}
                    style={{
                      padding: '12px 16px',
                      borderRight: '1px solid #d1d5db',
                      borderBottom: '1px solid #d1d5db',
                      backgroundColor: '#f3f4f6',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {column}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {activeTab.data.map((row, rowIndex) => (
                <tr key={rowIndex}>
                  <td
                    style={{
                      padding: '12px 16px',
                      borderRight: '1px solid #e5e7eb',
                      borderBottom: '1px solid #e5e7eb',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {rowIndex + 1}
                  </td>

                  {columns.map((column) => (
                    <td
                      key={`${rowIndex}-${column}`}
                      style={{
                        padding: '12px 16px',
                        borderRight: '1px solid #e5e7eb',
                        borderBottom: '1px solid #e5e7eb',
                        whiteSpace: column === '에러메시지' ? 'normal' : 'nowrap',
                      }}
                    >
                      {renderCellValue(row[column])}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Box>
    </Box>
  )
}

export default DeviceUpdate
